import { useEffect, useState } from 'react';

import { useTokenResult } from '@/hooks/tokenQueries/useTokenResult';

import type { SearchComboBoxItemProps } from '../SearchComboBox/SearchComboBoxItem';
import { Chart } from './Chart';
import { Metadata } from './Metadata';
import { Scores } from './Scores';

export type PreviewToken = Omit<SearchComboBoxItemProps, 'setPreviewToken'>;

interface TokenPreviewPopoverProps extends PreviewToken {
  isVisible: boolean;
  onExitComplete?: () => void;
}

export const TokenPreviewPopover = ({
  address,
  chainId,
  isVisible,
  onExitComplete,
}: TokenPreviewPopoverProps): React.ReactElement | null => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const { token } = useTokenResult(address, chainId);
  const animationDuration = 200;

  useEffect(() => {
    if (isVisible) {
      setShouldRender(true);
      const timer = setTimeout(() => {
        setIsAnimating(true);
      }, 10);
      return () => clearTimeout(timer);
    } else {
      setIsAnimating(false);
      const timer = setTimeout(() => {
        setShouldRender(false);
        onExitComplete?.();
      }, animationDuration);
      return () => clearTimeout(timer);
    }
  }, [isVisible, onExitComplete]);

  if (!shouldRender) return null;

  return (
    <div
      className="rounded-[1.25rem] border-1 absolute z-0 bg-special-background-modal border-special-border-avatar shadow-[0px_8px_36px_rgba(55,65,81,0.15)] h-[300px] w-[223px] -mr-[231px] mt-[129px] right-0 top-[calc(50%-65px)] -translate-y-[50%]"
      style={{
        opacity: isAnimating ? 1 : 0,
        transform: `translateY(-50%) scale(${isAnimating ? 1 : 0.95})`,
        transition: `opacity ${animationDuration}ms ease-in-out, transform ${animationDuration}ms ease-in-out`,
        transformOrigin: 'left center',
      }}
    >
      <Metadata token={token} />
      <Scores token={token} />
      <Chart token={token} />
    </div>
  );
};
